import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { isTicketConversation } from 'dashboard/helper/conversationLens';

// `ticketLabel` is the same suggestion worded for a thread the agent opened as
// a ticket. Only the label changes - the prompt sent to the assistant is the
// same either way, since the model reasons over the same message history.
const ROUTE_PROMPT_MAP = {
  conversations: [
    {
      icon: 'i-lucide-text',
      label: 'CAPTAIN.COPILOT.PROMPTS.SUMMARIZE.LABEL',
      ticketLabel: 'CAPTAIN.COPILOT.PROMPTS.SUMMARIZE.LABEL_TICKET',
      chip: 'CAPTAIN.COPILOT.PROMPTS.SUMMARIZE.CHIP',
      description: 'CAPTAIN.COPILOT.PROMPTS.SUMMARIZE.DESCRIPTION',
      prompt: 'CAPTAIN.COPILOT.PROMPTS.SUMMARIZE.CONTENT',
    },
    {
      icon: 'i-lucide-pen-line',
      label: 'CAPTAIN.COPILOT.PROMPTS.SUGGEST.LABEL',
      chip: 'CAPTAIN.COPILOT.PROMPTS.SUGGEST.CHIP',
      description: 'CAPTAIN.COPILOT.PROMPTS.SUGGEST.DESCRIPTION',
      prompt: 'CAPTAIN.COPILOT.PROMPTS.SUGGEST.CONTENT',
    },
    {
      icon: 'i-lucide-star',
      label: 'CAPTAIN.COPILOT.PROMPTS.RATE.LABEL',
      ticketLabel: 'CAPTAIN.COPILOT.PROMPTS.RATE.LABEL_TICKET',
      chip: 'CAPTAIN.COPILOT.PROMPTS.RATE.CHIP',
      description: 'CAPTAIN.COPILOT.PROMPTS.RATE.DESCRIPTION',
      prompt: 'CAPTAIN.COPILOT.PROMPTS.RATE.CONTENT',
    },
  ],
  dashboard: [
    {
      icon: 'i-lucide-flame',
      label: 'CAPTAIN.COPILOT.PROMPTS.HIGH_PRIORITY.LABEL',
      chip: 'CAPTAIN.COPILOT.PROMPTS.HIGH_PRIORITY.CHIP',
      description: 'CAPTAIN.COPILOT.PROMPTS.HIGH_PRIORITY.DESCRIPTION',
      prompt: 'CAPTAIN.COPILOT.PROMPTS.HIGH_PRIORITY.CONTENT',
    },
    {
      icon: 'i-lucide-users',
      label: 'CAPTAIN.COPILOT.PROMPTS.LIST_CONTACTS.LABEL',
      chip: 'CAPTAIN.COPILOT.PROMPTS.LIST_CONTACTS.CHIP',
      description: 'CAPTAIN.COPILOT.PROMPTS.LIST_CONTACTS.DESCRIPTION',
      prompt: 'CAPTAIN.COPILOT.PROMPTS.LIST_CONTACTS.CONTENT',
    },
  ],
};

export function useCopilotPrompts() {
  const { t } = useI18n();
  const route = useRoute();

  const currentChat = useMapGetter('getSelectedChat');
  const isTicket = computed(() => isTicketConversation(currentChat.value));

  // The tickets screen opens the copilot against a single thread, exactly like
  // the conversations screen, so it gets the thread-level prompts rather than
  // the account-wide ones.
  const promptOptions = computed(() => {
    const scope =
      route.path.includes('/conversations') || route.path.includes('/tickets')
        ? 'conversations'
        : 'dashboard';
    return ROUTE_PROMPT_MAP[scope].map(opt => ({
      icon: opt.icon,
      label: t(isTicket.value && opt.ticketLabel ? opt.ticketLabel : opt.label),
      chip: t(opt.chip),
      description: t(opt.description),
      prompt: t(opt.prompt),
    }));
  });

  return { promptOptions };
}
